/**
 * VideoRenderer - Unified interface for video playback renderers
 *
 * This interface defines the contract that both legacy BBC VideoContext
 * and modern Timeloom renderer must implement to ensure seamless switching
 * and compatibility.
 *
 * All video renderer implementations MUST implement this interface to be
 * compatible with the audio editor's sequencer system.
 */

/**
 * VideoRendererType enum
 *
 * IMPORTANT: These values MUST match the VIDEO_RENDERER_TYPES enum in Utils.js
 * Utils.js is the single source of truth for these values across the application.
 * See Utils.getInstance().getVideoRendererTypes()
 *
 * TypeScript requires literal types for type safety, so we duplicate the values here.
 * Any changes to Utils.js VIDEO_RENDERER_TYPES must be reflected here.
 */
export enum VideoRendererType {
    VideoContext = 'VideoContext',
    Timeloom = 'Timeloom'
}

export type VideoRendererEventHandler = (event: string, handler: (...args: unknown[]) => void) => void;

export interface VideoRenderer {
    /**
     * Renderer type identifier for explicit type checking
     * Replaces brittle duck-typing with explicit type property
     */
    readonly rendererType: VideoRendererType;

    /**
     * Start video playback from current position
     * @returns Promise that resolves when playback starts, or void for sync start
     */
    play(): Promise<void> | void;

    /**
     * Pause video playback at current position
     * @returns Promise that resolves when paused, or void for sync pause
     */
    pause(): Promise<void> | void;

    /**
     * Seek to specific time position
     * @param timeMs - Time position in milliseconds
     * @returns Promise that resolves when seek completes, or void for sync seek
     */
    seek(timeMs: number): Promise<void> | void;

    /**
     * Get current playback position
     * @returns Current time in milliseconds
     */
    getCurrentTime(): number;

    /**
     * Destroy renderer and clean up resources
     * MUST remove all event listeners, cancel pending operations, and release memory
     */
    destroy(): void;

    /**
     * Register event handler
     * Supported events: 'play', 'pause', 'seek', 'ended', 'error'
     * @param event - Event name
     * @param handler - Event handler function
     */
    on?(event: string, handler: (...args: unknown[]) => void): void;

    /**
     * Unregister event handler
     * @param event - Event name
     * @param handler - Event handler function to remove
     */
    off?(event: string, handler: (...args: unknown[]) => void): void;
}

/**
 * Minimal Timeline interface for type safety
 * Represents the core properties/methods we rely on from timeloom Timeline
 */
export interface MinimalTimeline {
    currentTime: number;
    duration?: number;
    play(): void | Promise<void>;
    pause(): void | Promise<void>;
    seek?(timeMs: number): void | Promise<void>;
}

/**
 * Timeloom-specific extensions to base VideoRenderer interface
 * These methods are only available when rendererType === VideoRendererType.Timeloom
 */
export interface TimeloomRenderer extends VideoRenderer {
    rendererType: VideoRendererType.Timeloom;

    /**
     * Get the underlying Timeloom Timeline object
     * @returns Timeline instance or null if not initialized
     */
    getTimeline(): MinimalTimeline | null;

    /**
     * Refresh the timeline with new data
     * @returns Promise that resolves when timeline is updated
     */
    refresh(): Promise<void>;
}

/**
 * BBC VideoContext-specific extensions
 * These methods are only available when rendererType === VideoRendererType.VideoContext
 */
export interface LegacyVideoContextRenderer extends VideoRenderer {
    rendererType: VideoRendererType.VideoContext;

    /**
     * Legacy VideoContext-specific play method
     */
    play(): void;

    /**
     * Legacy VideoContext-specific pause method
     */
    pause(): void;

    /**
     * Legacy VideoContext-specific seek method
     */
    seek(timeMs: number): void;
}

/**
 * Type guard to check if renderer is Timeloom
 */
export function isTimeloomRenderer(renderer: VideoRenderer): renderer is TimeloomRenderer {
    return renderer.rendererType === VideoRendererType.Timeloom;
}

/**
 * Type guard to check if renderer is VideoContext
 */
export function isVideoContextRenderer(renderer: VideoRenderer): renderer is LegacyVideoContextRenderer {
    return renderer.rendererType === VideoRendererType.VideoContext;
}

/**
 * Safely call play() method handling both sync and async implementations
 */
export async function safePlay(renderer: VideoRenderer): Promise<void> {
    const result = renderer.play();
    if (result instanceof Promise) {
        await result;
    }
}

/**
 * Safely call pause() method handling both sync and async implementations
 */
export async function safePause(renderer: VideoRenderer): Promise<void> {
    const result = renderer.pause();
    if (result instanceof Promise) {
        await result;
    }
}

/**
 * Safely call seek() method handling both sync and async implementations
 */
export async function safeSeek(renderer: VideoRenderer, timeMs: number): Promise<void> {
    const result = renderer.seek(timeMs);
    if (result instanceof Promise) {
        await result;
    }
}
